// What an investigation offers the reader to steer it by, before it starts:
// what met their standards, what slipped past every standard loudest, where
// it happened most, what deserves a second look, and what they asked for
// last time (already checked). Each steer is a line of the reader's first
// message to the coordinator; drawing them costs no model call.
import { SET_ASIDE } from "./investigateFacts.js";
import { severityRank } from "./triageLite.js";

export const STEER_GROUPS = [
    { id: "last", label: "From your last investigation" },
    { id: "met", label: "What met your standards" },
    { id: "loud", label: "Loud, and past every standard" },
    { id: "where", label: "Where it happened most" },
    { id: "check", label: "Worth a second look" },
    { id: "common", label: "Often asked" },
];
const COMMON = ["Credentials and secrets", "Production systems", "Anything leaving the network"];
const LOUD = 3; // high and above
const str = (v) => (v == null ? "" : String(v));
const plural = (n, one, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

// A finding's kind of act as the lens names it (its conduct family), else
// its category made readable.
const kindOf = (finding, nameOf) => {
    const named = str(nameOf?.conduct?.(finding)).trim();
    if (named) return named;
    const raw = str(finding.category).replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim();
    return raw ? `${raw[0].toUpperCase()}${raw.slice(1)}` : "";
};

function top(counts, n) {
    return [...counts].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).slice(0, n);
}

// `snapshot`: the scope as investigators see it (investigateSwarm snapshotOf).
// `inbox`: the case inbox's, which can also audit what the standards let
// through and raises rare acts.
export function steersFor(snapshot, { nameOf = null, inbox = false } = {}) {
    const { built, placed, investigation } = snapshot;
    const out = [];
    for (const p of investigation?.priorities || []) out.push({ group: "last", text: p, checked: true });
    if (inbox) {
        const rare = built.cases.filter((c) => c.origin === "rare" && !c.dig && !c.decision).length;
        if (rare) out.push({ group: "loud", text: "The rare acts no standard caught", why: plural(rare, "case") });
        const letThrough = placed.filter((p) => ["slipped", "allowed", "contained"].includes(p.group)).length;
        if (letThrough) out.push({ group: "check", text: "Audit a sample of what the standards let through", why: `${plural(letThrough, "finding")} let through` });
    }

    const met = new Map();
    for (const c of built.cases.filter((x) => x.group === "needs_you")) for (const name of c.metBy || []) met.set(name, (met.get(name) || 0) + 1);
    for (const [name, n] of top(met, 4)) out.push({ group: "met", text: `Start with what met ${name}`, why: plural(n, "case") });

    const loud = placed.filter((p) => p.group === "slipped" && severityRank(p.finding.severity) >= LOUD);
    const byCategory = new Map();
    for (const p of loud) {
        const kind = kindOf(p.finding, nameOf);
        if (kind) byCategory.set(kind, (byCategory.get(kind) || 0) + 1);
    }
    for (const [category, n] of top(byCategory, 3)) out.push({ group: "loud", text: category, why: `${plural(n, "loud finding")} no standard caught` });

    const byMachine = new Map();
    for (const p of placed.filter((x) => (x.group === "needs_you" || x.group === "slipped") && severityRank(x.finding.severity) >= LOUD)) {
        const endpoint = str(p.finding.endpoint).toLowerCase();
        if (endpoint) byMachine.set(endpoint, (byMachine.get(endpoint) || 0) + 1);
    }
    for (const [endpoint, n] of top(byMachine, 2)) {
        const machine = nameOf?.machine?.(endpoint) || `machine ${endpoint.slice(0, 8)}`;
        out.push({ group: "where", text: `What happened on ${machine}`, why: plural(n, "loud finding") });
    }

    const asideLoud = placed.filter((p) => SET_ASIDE.has(p.group) && severityRank(p.finding.severity) >= LOUD).length;
    if (asideLoud) out.push({ group: "check", text: "Whether the loud findings set aside really were harmless", why: `${plural(asideLoud, "loud finding")} set aside` });
    const unchecked = placed.filter((p) => p.group === "unchecked").length;
    if (unchecked) out.push({ group: "check", text: "The findings no check has finished for", why: plural(unchecked, "finding") });

    for (const text of COMMON) out.push({ group: "common", text });

    const seen = new Set();
    return out.filter((s) => {
        const key = s.text.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    }).map((s, i) => ({ id: `st-${i + 1}`, ...s }));
}
